import type { ApiKeyUsageDetail, ApiResponse, ModelUsageDetail, OpenAiUsagePage, OpenAiUsageResult } from './types';
import { formatUtcDate } from './client';
import {
  buildProjectDayKey,
  createUnresolvedApiKeyUsage,
  ensureDailyEntry,
  type ProjectDayKeyUsage,
  type UsageAggregate,
} from './usage-aggregation';
import {
  OPENAI_WEB_SEARCH_COST_MODEL,
  OPENAI_WEB_SEARCH_COST_PER_CALL,
  isOpenAiWebSearchLineItem,
} from './web-search';

interface WeightedAllocation<T> {
  item: T;
  weight: number;
}

interface WebSearchCostGroup {
  date: string;
  projectId: string;
  cost: number;
  calls: number | null;
}

export interface WebSearchCostAllocationResult {
  allocatedCost: number;
  errors: string[];
}

function allocateExact<T>(amount: number, candidates: WeightedAllocation<T>[]): Array<{ item: T; amount: number }> {
  if (candidates.length === 0) return [];
  const positiveTotal = candidates.reduce((sum, candidate) => (
    sum + (Number.isFinite(candidate.weight) && candidate.weight > 0 ? candidate.weight : 0)
  ), 0);
  const denominator = positiveTotal > 0 ? positiveTotal : candidates.length;
  let remaining = amount;

  return candidates.map((candidate, index) => {
    const weight = positiveTotal > 0
      ? Math.max(0, Number.isFinite(candidate.weight) ? candidate.weight : 0)
      : 1;
    const allocated = index === candidates.length - 1
      ? remaining
      : amount * weight / denominator;
    remaining -= allocated;
    return { item: candidate.item, amount: allocated };
  });
}

function addWebSearchCostToApiKey(
  apiKeyUsage: ApiKeyUsageDetail,
  date: string,
  cost: number
): void {
  apiKeyUsage.cost += cost;
  apiKeyUsage.costQuality = 'estimated';
  if (apiKeyUsage.firstSeen === 'N/A' || date < apiKeyUsage.firstSeen) apiKeyUsage.firstSeen = date;
  if (apiKeyUsage.lastSeen === 'N/A' || date > apiKeyUsage.lastSeen) apiKeyUsage.lastSeen = date;

  const modelUsage: ApiKeyUsageDetail['modelUsage'][string] & { lastUsed?: string } =
    apiKeyUsage.modelUsage[OPENAI_WEB_SEARCH_COST_MODEL] ?? {
      model: OPENAI_WEB_SEARCH_COST_MODEL,
      contextTokens: 0,
      generatedTokens: 0,
      cachedTokens: 0,
      cost: 0,
      numberOfRequests: 0,
      provider: 'openai',
    };
  modelUsage.cost += cost;
  modelUsage.lastUsed = date;
  apiKeyUsage.modelUsage[OPENAI_WEB_SEARCH_COST_MODEL] = modelUsage;
  apiKeyUsage.modelCosts[OPENAI_WEB_SEARCH_COST_MODEL] =
    (apiKeyUsage.modelCosts[OPENAI_WEB_SEARCH_COST_MODEL] ?? 0) + cost;
  if (!apiKeyUsage.dailyModelCosts) apiKeyUsage.dailyModelCosts = {};
  if (!apiKeyUsage.dailyModelCosts[date]) apiKeyUsage.dailyModelCosts[date] = {};
  apiKeyUsage.dailyModelCosts[date][OPENAI_WEB_SEARCH_COST_MODEL] =
    (apiKeyUsage.dailyModelCosts[date][OPENAI_WEB_SEARCH_COST_MODEL] ?? 0) + cost;
}

function parseSearchCalls(quantity: OpenAiUsageResult['quantity']): number | null {
  if (quantity === undefined || quantity === null || String(quantity).trim() === '') return null;
  const calls = Number(quantity);
  return Number.isSafeInteger(calls) && calls >= 0 ? calls : null;
}

function parseCost(result: OpenAiUsageResult, calls: number | null): number | null {
  const rawAmount = result?.amount?.value;
  const amount = rawAmount === undefined || rawAmount === null ? Number.NaN : Number(rawAmount);
  if (Number.isFinite(amount) && amount >= 0) return amount;

  return calls === null ? null : calls * OPENAI_WEB_SEARCH_COST_PER_CALL;
}

/**
 * Reconcile project-level OpenAI web-search tool charges into key/model/day
 * detail. The Costs API amount is authoritative for the project/day; key and
 * hour attribution is estimated from directly observed `webrun` markers, then
 * falls back to completion request share when the marker is absent.
 */
export function applyProjectWebSearchCosts(
  aggregate: UsageAggregate,
  costsUsage: ApiResponse<OpenAiUsagePage> | null
): WebSearchCostAllocationResult {
  if (!costsUsage?.success || !Array.isArray(costsUsage.data?.data)) {
    const errors = costsUsage === null
      ? Array.from(aggregate.projectDayUsageMap.values())
          .filter(projectUsage => Array.from(projectUsage.keys.values())
            .some(keyUsage => keyUsage.webRunRequests > 0))
          .map(projectUsage => (
            `Web search costs unavailable for partial-day range in project ${projectUsage.projectId} on ${projectUsage.date}`
          ))
      : [];
    return { allocatedCost: 0, errors };
  }

  const costGroups = new Map<string, WebSearchCostGroup>();
  for (const bucket of costsUsage.data.data) {
    const startSeconds = Number(bucket?.start_time ?? bucket?.startTime);
    if (!Number.isFinite(startSeconds)) continue;
    const date = formatUtcDate(new Date(startSeconds * 1000));
    for (const result of Array.isArray(bucket?.results) ? bucket.results : []) {
      if (!isOpenAiWebSearchLineItem(result?.line_item ?? result?.lineItem)) continue;
      const projectId = result?.project_id ?? result?.projectId;
      if (typeof projectId !== 'string' || !projectId) continue;

      const calls = parseSearchCalls(result.quantity);
      const cost = parseCost(result, calls);
      if (cost === null) continue;

      const key = buildProjectDayKey(date, projectId);
      const current = costGroups.get(key) ?? { date, projectId, cost: 0, calls: 0 };
      current.cost += cost;
      current.calls = current.calls === null || calls === null ? null : current.calls + calls;
      costGroups.set(key, current);
    }
  }

  const errors: string[] = [];
  let allocatedCost = 0;
  for (const [groupKey, group] of costGroups) {
    const projectUsage = aggregate.projectDayUsageMap.get(groupKey);
    const allKeyUsage = projectUsage ? Array.from(projectUsage.keys.values()) : [];
    const directWebRunKeys = allKeyUsage.filter(keyUsage => keyUsage.webRunRequests > 0);
    const completionKeys = allKeyUsage.filter(keyUsage => keyUsage.requests > 0);
    const selectedKeys = (directWebRunKeys.length > 0 ? directWebRunKeys : completionKeys)
      .sort((a, b) => a.apiKeyId.localeCompare(b.apiKeyId));
    const useWebRunWeight = directWebRunKeys.length > 0;

    let keyCandidates: Array<WeightedAllocation<ProjectDayKeyUsage>> = selectedKeys.map(keyUsage => ({
      item: keyUsage,
      weight: useWebRunWeight ? keyUsage.webRunRequests : keyUsage.requests,
    }));

    if (keyCandidates.length === 0) {
      const unresolvedKeyId = `openai-unattributed-web-search:${group.projectId}`;
      let unresolved = aggregate.apiKeyUsageMap.get(unresolvedKeyId);
      if (!unresolved) {
        unresolved = createUnresolvedApiKeyUsage(unresolvedKeyId, group.date);
        unresolved.name = `Unattributed OpenAI web-search cost (${group.projectId})`;
        unresolved.projectId = group.projectId;
        unresolved.projectIds = [group.projectId];
        unresolved.sourceIds = [unresolvedKeyId];
        aggregate.apiKeyUsageMap.set(unresolvedKeyId, unresolved);
      }
      keyCandidates = [{
        item: {
          apiKeyId: unresolvedKeyId,
          requests: 0,
          webRunRequests: 0,
          buckets: new Map([[group.date, { requests: 1, webRunRequests: 0 }]]),
        },
        weight: 1,
      }];
      errors.push(`Web search cost could not be attributed to an API key for project ${group.projectId} on ${group.date}`);
    }

    for (const allocation of allocateExact(group.cost, keyCandidates)) {
      const apiKeyUsage = aggregate.apiKeyUsageMap.get(allocation.item.apiKeyId)!;
      addWebSearchCostToApiKey(apiKeyUsage, group.date, allocation.amount);
    }

    const bucketCandidates: Array<WeightedAllocation<{ bucketKey: string; apiKeyId: string }>> = [];
    for (const candidate of keyCandidates) {
      for (const [bucketKey, bucketUsage] of candidate.item.buckets) {
        const weight = useWebRunWeight ? bucketUsage.webRunRequests : bucketUsage.requests;
        if (weight > 0) {
          bucketCandidates.push({ item: { bucketKey, apiKeyId: candidate.item.apiKeyId }, weight });
        }
      }
    }
    if (bucketCandidates.length === 0) {
      bucketCandidates.push({ item: { bucketKey: group.date, apiKeyId: keyCandidates[0].item.apiKeyId }, weight: 1 });
    }

    for (const allocation of allocateExact(group.cost, bucketCandidates)) {
      const dailyData = ensureDailyEntry(aggregate, allocation.item.bucketKey);
      dailyData.cost += allocation.amount;
      dailyData.apiKeys.add(allocation.item.apiKeyId);
      if (!dailyData.modelCosts) dailyData.modelCosts = {};
      dailyData.modelCosts[OPENAI_WEB_SEARCH_COST_MODEL] =
        (dailyData.modelCosts[OPENAI_WEB_SEARCH_COST_MODEL] ?? 0) + allocation.amount;
      if (!dailyData.breakdowns) dailyData.breakdowns = {};
      if (!dailyData.breakdowns.openai) {
        dailyData.breakdowns.openai = {
          provider: 'openai',
          requests: 0,
          cost: 0,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          isEstimatedRequests: false,
        };
      }
      dailyData.breakdowns.openai.cost += allocation.amount;
    }

    const modelUsage: ModelUsageDetail = aggregate.modelUsageMap.get(OPENAI_WEB_SEARCH_COST_MODEL) ?? {
      model: OPENAI_WEB_SEARCH_COST_MODEL,
      contextTokens: 0,
      generatedTokens: 0,
      cachedTokens: 0,
      cost: 0,
      numberOfRequests: 0,
      provider: 'openai',
    };
    modelUsage.cost += group.cost;
    modelUsage.webSearchCalls = modelUsage.webSearchCalls === null || group.calls === null
      ? null
      : (modelUsage.webSearchCalls ?? 0) + group.calls;
    modelUsage.lastUsed = group.date;
    aggregate.modelUsageMap.set(OPENAI_WEB_SEARCH_COST_MODEL, modelUsage);
    aggregate.totals.cost += group.cost;
    allocatedCost += group.cost;
  }

  for (const [usageKey, projectUsage] of aggregate.projectDayUsageMap) {
    const webRunRequests = Array.from(projectUsage.keys.values())
      .reduce((sum, keyUsage) => sum + keyUsage.webRunRequests, 0);
    if (webRunRequests > 0 && !costGroups.has(usageKey)) {
      errors.push(`Web search costs unavailable for project ${projectUsage.projectId} on ${projectUsage.date}`);
    }
  }

  return { allocatedCost, errors };
}
